import { z } from "zod";

/**
 * Permissive JSON-like value schema. Accepts primitives, arrays, and plain
 * objects without recursive validation.
 */
export const jsonish = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.unknown()),
  z.record(z.string(), z.unknown()),
]);
export type Jsonish = z.infer<typeof jsonish>;

/**
 * A repeated query param, which travels as `?foo=a,b,c` because Hono's zod
 * validator can't express real array params. Each member is parsed with
 * `member`, so pass `z.coerce.number()` for ids, a `z.enum` for a fixed
 * vocabulary, or `z.string()` for free-form values.
 *
 * An unrecognized member is a 400 rather than a silent drop, so a typo'd filter
 * can't quietly widen a result set. Blank and absent are the same input — a
 * query string can't distinguish them.
 *
 * @param name - used in the rejection message, so it should match the param
 * @param whenBlank - value for an absent or blank param; defaults to empty
 * @param ignore - members accepted and discarded rather than rejected, for
 *   values a still-deployed frontend sends that the server no longer models
 */
export const arrayQueryParam = <T>(
  member: z.ZodType<T>,
  name: string,
  { whenBlank = [], ignore }: { whenBlank?: T[]; ignore?: (raw: string) => boolean } = {},
) =>
  z
    .string()
    .optional()
    .transform((str, ctx): T[] => {
      if (!str || str.trim() === "") return whenBlank;
      const result: T[] = [];
      for (const raw of str.split(",").map((s) => s.trim())) {
        if (raw === "" || ignore?.(raw)) continue;
        const parsed = member.safeParse(raw);
        if (!parsed.success) {
          ctx.addIssue({ code: "custom", message: `Invalid ${name}: ${raw}` });
          return z.NEVER;
        }
        result.push(parsed.data);
      }
      return result;
    });

/** https://www.geodev.me/blog/deeppartial-in-typescript */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * Build a `Record` over a closed key list, one value per key.
 *
 * `Object.fromEntries` can only promise `Record<string, V>`, because it cannot
 * know the entries cover every key. This is the one place that knowledge is
 * applied: `keys` is the full key type by construction (an `as const` tuple is
 * what callers pass), and each key is visited exactly once, so the result is
 * complete. Callers get a checked record without restating the assertion.
 */
export const recordOf = <K extends string, V>(
  keys: readonly K[],
  valueFor: (key: K) => V,
): Record<K, V> => {
  const record: Partial<Record<K, V>> = {};
  for (const key of keys) record[key] = valueFor(key);
  return record as Record<K, V>;
};

/** Unlike an intersection, ignores the overlapping keys from the base object, instead of merging their types. */
export type Override<Base, OverrideWith> = Omit<Base, keyof OverrideWith> & OverrideWith;

/**
 * Decode base64 → string as UTF-8. `atob` alone yields a Latin-1 byte string
 * that mangles any multi-byte character; reinterpret those bytes through
 * TextDecoder so emoji / CJK round-trip. `fatal` makes invalid UTF-8 throw so
 * the caller surfaces garbage as a rejection instead of a U+FFFD-mangled parse.
 * Browser-safe (no node:buffer), since this file is symlinked into `web/`.
 */
const decodeBase64Utf8 = (str: string): string =>
  new TextDecoder("utf-8", { fatal: true }).decode(
    Uint8Array.from(atob(str), (ch) => ch.charCodeAt(0)),
  );

/**
 * Preprocess base64 JSON strings, to pass through to a real schema.
 *
 * ```ts
 * const mySchema = z.object({
 *   // this field is stringified JSON, we want to JSON.parse it and then manage its type
 *   foo: base64JsonStringSchema.pipe(z.array(z.object({
 *     bar: z.string(),
 *     baz: z.string(),
 *   }))),
 * });
 * ```
 *
 * https://github.com/colinhacks/zod/discussions/2215#discussioncomment-5356276
 */
export const base64JsonStringSchema = z.string().transform((str, ctx) => {
  try {
    return JSON.parse(decodeBase64Utf8(str));
  } catch {
    ctx.addIssue({ code: "custom", message: "Invalid base64 JSON string" });
    return z.NEVER;
  }
});

/**
 * Makes specified fields required in a type while leaving other fields unchanged.
 *
 * @example
 * type User = {
 *   id?: string;
 *   name?: string;
 *   email?: string;
 * }
 *
 * type UserWithRequiredId = PartialRequired<User, 'id'>
 * // Result: { id: string; name?: string; email?: string; }
 *
 * type UserWithRequiredIdAndName = PartialRequired<User, 'id' | 'name'>
 * // Result: { id: string; name: string; email?: string; }
 */
export type PartialRequired<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

/** Omit that distributes over union types (built-in Omit only operates on common keys). */
export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** Partial that distributes over union types (built-in Partial only operates on common keys). */
export type DistributivePartial<T> = T extends unknown ? Partial<T> : never;

/** Union of the value types of an object type. Mirror of `keyof T`. */
export type ValueOf<T> = T[keyof T];

/** For a given function, get all but the first parameter. */
export type TailParameters<T extends (first: any, ...rest: any[]) => any> = T extends (
  first: any,
  ...rest: infer R
) => any
  ? R
  : never;
